import type { Transaction, User } from 'payments-api/model';
import {
  useGetTransactions,
  usePatchTransactionsTransactionRef,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { useToast } from 'components/ToastManager';
import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { ItemTableItem } from './ItemTable';
import { FormatBillingAmount } from './formatBillingAmount';
import { PaymentsModal } from './PaymentsModal';
import { UserColorDot } from './UserColorDot';

/** Rounded grey triangle badge matching the account list icon style. */
const TxTriangleBadge = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" className="shrink-0">
    <path
      d="M6.13 1.5a1 1 0 0 1 1.74 0l5.2 9a1 1 0 0 1-.87 1.5H1.8a1 1 0 0 1-.87-1.5l5.2-9Z"
      fill="rgb(var(--colour-main-300))"
    />
  </svg>
);

type ReconcileStep = 'transaction' | 'user';

type ReconcileModalProps = {
  invoiceId: string;
  items: ItemTableItem[];
  users: User[];
  onClose: VoidFunction;
};

export const ReconcileModal = ({
  invoiceId,
  items,
  users,
  onClose,
}: ReconcileModalProps) => {
  const [step, setStep] = useState<ReconcileStep>('transaction');
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading } = useGetTransactions({
    reconciliation_status: 'unreconciled',
  });
  const patchTransactionMutation = usePatchTransactionsTransactionRef();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const transactions = useMemo(() => data?.data ?? [], [data]);

  const filteredTransactions = useMemo(() => {
    if (searchQuery.trim() === '') return transactions;
    const q = searchQuery.toLowerCase();
    return transactions.filter((txn) =>
      txn.external_id.toLowerCase().includes(q)
    );
  }, [transactions, searchQuery]);

  // Filter users based on transaction amount sign
  const availableUsers = useMemo(() => {
    if (!selectedTransaction) return [];
    const isPositive = Number(selectedTransaction.amount) > 0;
    const relevantType = isPositive ? 'payin' : 'payout';
    const relevantUserIds = new Set(
      items
        .filter((item) => item.type === relevantType)
        .map((item) => item.user_id)
        .filter(Boolean)
    );
    return users.filter(
      (u) => relevantUserIds.has(u.id) || relevantUserIds.has(u.external_id)
    );
  }, [selectedTransaction, items, users]);

  const handleSelectTransaction = (txn: Transaction) => {
    setSelectedTransaction(txn);
    setStep('user');
  };

  const handleSelectUser = async (userId: string) => {
    if (!selectedTransaction) return;

    const isPositive = Number(selectedTransaction.amount) > 0;
    const allocationType = isPositive ? 'invoice_payin' : 'invoice_payout';

    try {
      await patchTransactionMutation.mutateAsync({
        transactionRef: selectedTransaction.id,
        data: {
          current_transaction_version: selectedTransaction.version,
          allocations: {
            create: [
              {
                invoice_id: invoiceId,
                amount: selectedTransaction.amount,
                user: { id: userId },
                type: allocationType,
              },
            ],
          },
        },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['/transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['/invoices'] }),
      ]);
      showToast(<Toast type="success" message="Transaction reconciled" />);
      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to reconcile transaction.';
      showToast(<Toast type="error" message={message} />);
    }
  };

  const backButton = (
    <button
      type="button"
      className="absolute left-f1 top-1/2 -translate-y-1/2 font-FragmentMono text-main-500 hover:text-main cursor-pointer text-xmd"
      onClick={() => setStep('transaction')}
    >
      ←
    </button>
  );

  const renderHeader = (title: string, showBack: boolean) => (
    <div className="relative flex items-center justify-center w-full text-main text-xmd font-helvetica">
      {showBack && backButton}
      <span>{title}</span>
    </div>
  );

  const renderTransactionStep = () => (
    <div className="flex flex-col w-full text-xmd font-helvetica">
      {/* Search bar */}
      <div className="pb-f1">
        <TextInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search"
          size="xmd"
          width="w-full"
          height="h-f4"
          padding="px-f1"
          align="left"
          border
          background="!bg-main-100 hover:!bg-main-100 focus:!bg-main-100"
          extraClassNames="!border-[0.5px] !border-main-200 focus:!border-main-200"
          data-testid="reconcile-search-input"
        />
      </div>
      <div className="border-[0.5px] border-main-200">
        {/* Add New row */}
        <button
          type="button"
          className={clsx(
            'flex flex-row items-center justify-between px-f1 h-f4 w-full bg-main-100 hover:bg-negative cursor-pointer',
            'border-b-[0.5px] border-main-200'
          )}
          disabled
          data-testid="reconcile-add-new"
        >
          <div className="flex items-center gap-x-f1">
            <TxTriangleBadge />
            <span className="text-main">Add New</span>
          </div>
          <span className="text-main-500">+</span>
        </button>
        {(() => {
          if (isLoading) {
            return ['a', 'b', 'c', 'd'].map((id, i) => (
              <div
                key={`skeleton-${id}`}
                className={clsx(
                  'flex flex-row items-center px-f1 h-f4 w-full',
                  i < 3 && 'border-b-[0.5px] border-main-200'
                )}
              />
            ));
          }
          if (filteredTransactions.length === 0) {
            return (
              <div className="px-f1 py-f1 text-main-500">
                {transactions.length === 0
                  ? 'No unreconciled transactions'
                  : 'No matching transactions'}
              </div>
            );
          }
          return filteredTransactions.map((txn, i) => {
            const code = txn.currency as CurrencyCode;
            const precision = currencyPrecisionMap[code] || 2;
            return (
              <button
                key={txn.id}
                type="button"
                className={clsx(
                  'flex flex-row items-center justify-between px-f1 h-f4 w-full hover:bg-negative cursor-pointer',
                  i < filteredTransactions.length - 1 &&
                    'border-b-[0.5px] border-main-200'
                )}
                onClick={() => handleSelectTransaction(txn)}
                data-testid={`reconcile-txn-${txn.id}`}
              >
                <div className="flex items-center gap-x-f1">
                  <TxTriangleBadge />
                  <span className="text-main">{txn.external_id}</span>
                </div>
                <FormatBillingAmount
                  amount={Math.abs(Number(txn.unallocated_amount))}
                  currencyCode={code}
                  currencyPrecision={precision}
                />
              </button>
            );
          });
        })()}
      </div>
    </div>
  );

  const renderUserStep = () => (
    <div className="flex flex-col w-full text-xmd font-helvetica">
      {/* Selected transaction summary */}
      {selectedTransaction && (
        <div className="flex flex-row items-center justify-between px-f1 h-f4 border-[0.5px] border-main-200">
          <div className="flex items-center gap-x-f1">
            <TxTriangleBadge />
            <span className="text-main">{selectedTransaction.external_id}</span>
          </div>
          <FormatBillingAmount
            amount={Math.abs(Number(selectedTransaction.unallocated_amount))}
            currencyCode={selectedTransaction.currency as CurrencyCode}
            currencyPrecision={
              currencyPrecisionMap[
                selectedTransaction.currency as CurrencyCode
              ] || 2
            }
          />
        </div>
      )}

      {/* User list */}
      <div className="border-[0.5px] border-t-0 border-main-200">
        {availableUsers.length === 0 ? (
          <div className="px-f1 py-f1 text-main-500">
            No matching users for this transaction
          </div>
        ) : (
          availableUsers.map((user, i) => (
            <button
              key={user.external_id}
              type="button"
              className={clsx(
                'flex flex-row items-center justify-between px-f1 h-f4 w-full hover:bg-negative cursor-pointer',
                i < availableUsers.length - 1 &&
                  'border-b-[0.5px] border-main-200'
              )}
              onClick={() => handleSelectUser(user.id)}
              disabled={patchTransactionMutation.isPending}
              data-testid={`reconcile-user-${user.external_id}`}
            >
              <div className="flex items-center gap-x-f1">
                <UserColorDot seed={user.external_id} />
                <span className="text-main">{user.external_id}</span>
              </div>
              <span className="text-main-500">
                {patchTransactionMutation.isPending ? '...' : 'Reconcile'}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  );

  const titles: Record<ReconcileStep, string> = {
    transaction: 'Select Transaction',
    user: 'Select User',
  };

  return (
    <PaymentsModal
      data-testid="reconcile-modal"
      onClose={onClose}
      width="sm"
      shouldCenter={false}
      header={renderHeader(titles[step], step !== 'transaction')}
      body={step === 'transaction' ? renderTransactionStep() : renderUserStep()}
    />
  );
};
